import React, { type FC, memo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import {
  EventType,
  getIconRotation,
  resolveEventTypeColorFor,
  resolveEventTypeIconFor,
  resolveEventTypeLabelFor,
  shouldRotateIcon,
} from '@/features/events/shared'
import { standardTooltipProps } from '@/common/components'
import type { BlockingBucket } from '../types'

const COUNT_CAP = 9
const RESTING_ICON_SIZE_PX = 26

interface BadgeOffsetPx {
  readonly top: number
  readonly right: number
}

/**
 * Per-bucket pixel offset for the top-right count badge. Negative `top`/`right`
 * push the badge OUTWARD (above and to the right of the button's corner); values
 * closer to 0 pull it inward toward the icon.
 *
 * `top` is UNIFORM across all buckets so every counter sits on the same
 * horizontal line (all buttons share the same height). `right` is per-shape and
 * controls horizontal distance only, since different glyphs fill their top-right
 * corner differently:
 * - Upward triangle (TOOL_PERMISSION_REQUEST, apex at top → corner empty): TIGHT,
 *   so a small `right` already reads as a clean gap.
 * - Circle (AGENT_QUESTIONS) / square (PLAN_APPROVAL_REQUEST): corner filled →
 *   pushed further out so the gap matches the triangle's visual distance.
 * - Inverted triangle (ERRORED_EVENT, wide base at top → corner filled): same
 *   filled-corner geometry as the square → pushed further out.
 *
 * Values in px — this map is the single fine-tune point.
 */
const BADGE_OFFSET_BY_BUCKET: Readonly<Record<BlockingBucket, BadgeOffsetPx>> = {
  [EventType.TOOL_PERMISSION_REQUEST]: { top: -2, right: -2 },
  [EventType.AGENT_QUESTIONS]: { top: -2, right: -9 },
  [EventType.PLAN_APPROVAL_REQUEST]: { top: -2, right: -9 },
  [EventType.ERRORED_EVENT]: { top: -2, right: -9 },
}

interface BlockingBucketCounterProps {
  readonly bucket: BlockingBucket
  readonly count: number
  readonly isActive: boolean
  readonly isDimmed: boolean
  readonly onOpen: (bucket: BlockingBucket, anchorEl: HTMLElement) => void
}

const BlockingBucketCounterComponent: FC<BlockingBucketCounterProps> = ({
  bucket,
  count,
  isActive,
  isDimmed,
  onOpen,
}) => {
  const theme = useTheme()

  if (count === 0) return null

  const Icon = resolveEventTypeIconFor(bucket)
  if (!Icon) return null

  const label = resolveEventTypeLabelFor(bucket)
  const color = resolveEventTypeColorFor(bucket, theme)
  const rotate = shouldRotateIcon(bucket)
  const rotationAngle = getIconRotation(bucket)
  const ariaLabel = `${count} ${label} pending`
  const displayCount = count > COUNT_CAP ? `${COUNT_CAP}+` : String(count)
  const badgeOffset = BADGE_OFFSET_BY_BUCKET[bucket]

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onOpen(bucket, event.currentTarget)
  }

  return (
    <Tooltip {...standardTooltipProps} title={`${count} ${label} pending`}>
      <Box
        component="button"
        type="button"
        onClick={handleClick}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isActive ? 'true' : 'false'}
        sx={counterButtonSx(isDimmed)}
      >
        <Box sx={iconContainerSx(rotate, rotationAngle, color, isActive)}>
          <Icon fontSize="inherit" />
        </Box>
        <Typography component="span" sx={countBadgeSx(badgeOffset)}>
          {displayCount}
        </Typography>
      </Box>
    </Tooltip>
  )
}

export const BlockingBucketCounter = memo(BlockingBucketCounterComponent)

const counterButtonSx = (isDimmed: boolean): SxProps<Theme> => ({
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  p: 0,
  m: 0,
  borderRadius: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  overflow: 'visible',
  opacity: isDimmed ? 0.65 : 1,
  transition: 'opacity 0.15s ease',
  '&:hover': {
    background: 'transparent',
    opacity: 1,
  },
  '&:active': {
    background: 'transparent',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
})

const iconContainerSx = (
  rotate: boolean,
  rotationAngle: number,
  color: string,
  isActive: boolean,
): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: RESTING_ICON_SIZE_PX,
  color,
  transform:
    [rotate ? `rotate(${rotationAngle}deg)` : null, isActive ? 'scale(1.3)' : null]
      .filter(Boolean)
      .join(' ') || 'none',
  transition: 'transform 0.15s ease',
})

// Absolutely-positioned notification badge anchored to the button's top-right
// corner. Positioned relative to the NON-scaling button (not the icon), so the
// icon's active-state scale(1.3) does not drag or distort the count. The
// per-bucket offset slides the badge outward/inward to clear each glyph.
const countBadgeSx = (offset: BadgeOffsetPx): SxProps<Theme> => ({
  position: 'absolute',
  top: offset.top,
  right: offset.right,
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1,
  textAlign: 'center',
  color: 'text.primary',
  pointerEvents: 'none',
})
